import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api, { chessAPI, platformAPI } from '../services/api';
import { buildInviteLink } from '../utils/inviteLinks';
import '../styles/Dashboard.css';

const FRIENDLY_OPTIONS = [
  { seconds: 60, label: '1 min', icon: '⚡' },
  { seconds: 180, label: '3 min', icon: '🔥' },
  { seconds: 300, label: '5 min', icon: '🎯' },
  { seconds: 600, label: '10 min', icon: '♟️' },
];

const BETTING_OPTIONS = [
  { seconds: 60, label: '1 min', icon: '⚡' },
  { seconds: 120, label: '2 min', icon: '🔥' },
  { seconds: 180, label: '3 min', icon: '🎯' },
  { seconds: 300, label: '5 min', icon: '♟️' },
];

const CREATE_KIND_BOT = 'bot';
const CREATE_KIND_FRIENDLY = 'friendly';
const CREATE_KIND_REAL = 'real';
const QUICK_BOT_NAMES_BY_LEVEL = {
  1: ['PawnPilot', 'RookieRook', 'KnightCub'],
  2: ['BishopByte', 'CastleCore', 'TempoTactician'],
  3: ['QueenOracle', 'TitanFork', 'EndgameX'],
};

export default function ChessLobby() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState('friendly');

  const { user, refreshUser } = useAuth();

  const [openMatches, setOpenMatches] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [ongoingMatches, setOngoingMatches] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});

  const [wager, setWager] = useState(50);
  const [minimumWager, setMinimumWager] = useState(50);
  const [opponentId, setOpponentId] = useState('');
  const [timeControlSeconds, setTimeControlSeconds] = useState(300);
  const [joinId, setJoinId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKind, setCreateKind] = useState(CREATE_KIND_FRIENDLY);
  const [roomSearch, setRoomSearch] = useState('');
  const [activeTab, setActiveTab] = useState('my');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quickSearching, setQuickSearching] = useState(false);

  const userId = Number(user?.id);
  const hasUserId = Number.isInteger(userId) && userId > 0;

  useEffect(() => {
    const nextMode = String(params.get('mode') || '').toLowerCase() === 'betting'
      ? 'betting'
      : 'friendly';
    setMode(nextMode);
  }, [params]);

  useEffect(() => {
    const fallback = mode === 'betting' ? 180 : 300;
    const currentList = mode === 'betting' ? BETTING_OPTIONS : FRIENDLY_OPTIONS;
    const tc = Number(timeControlSeconds);
    if (!currentList.some((o) => o.seconds === tc)) {
      setTimeControlSeconds(fallback);
    }
    setError('');
  }, [mode, timeControlSeconds]);

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setWager((prev) => Math.max(nextMin, Number(prev) || 0));
        }
      } catch {
        // ignore settings failure
      }
    };

    loadPlatformSettings();
  }, []);

  const options = mode === 'betting' ? BETTING_OPTIONS : FRIENDLY_OPTIONS;

  const allMatches = useMemo(() => {
    const combinedRaw = [...ongoingMatches, ...challenges, ...openMatches];
    const combinedMap = new Map();

    combinedRaw.forEach((m) => {
      const id = String(m?.id || '');
      if (!id) return;
      if (!combinedMap.has(id)) combinedMap.set(id, m);
    });

    const getSortTime = (m) => {
      const raw = m?.started_at || m?.created_at;
      const t = raw ? Date.parse(raw) : NaN;
      if (!Number.isNaN(t)) return t;
      return Number(m?.id) || 0;
    };

    return Array.from(combinedMap.values()).sort((a, b) => getSortTime(b) - getSortTime(a));
  }, [ongoingMatches, challenges, openMatches]);

  const myCreatedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.creator_id) === userId),
    [allMatches, userId]
  );

  const myJoinedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.opponent_id) === userId && Number(m.creator_id) !== userId),
    [allMatches, userId]
  );

  const availableMatches = useMemo(
    () => allMatches.filter((m) => Number(m.creator_id) !== userId && !m.opponent_id),
    [allMatches, userId]
  );

  const filteredAvailableMatches = useMemo(() => {
    const query = String(roomSearch || '').trim().toLowerCase();
    if (!query) return availableMatches;

    return availableMatches.filter((m) => {
      const id = String(m?.id || '').toLowerCase();
      const creator = String(m?.creator_username || '').toLowerCase();
      return id.includes(query) || creator.includes(query);
    });
  }, [availableMatches, roomSearch]);

  const liveMatches = useMemo(
    () => allMatches.filter((m) => m.status === 'in_progress' && Number(m.creator_id) !== userId && Number(m.opponent_id) !== userId),
    [allMatches, userId]
  );

  const filteredLiveMatches = useMemo(() => {
    const query = String(roomSearch || '').trim().toLowerCase();
    if (!query) return liveMatches;

    return liveMatches.filter((m) => {
      const id = String(m?.id || '').toLowerCase();
      const creator = String(m?.creator_username || m?.white_username || '').toLowerCase();
      const opponent = String(m?.opponent_username || m?.black_username || '').toLowerCase();
      return id.includes(query) || creator.includes(query) || opponent.includes(query);
    });
  }, [liveMatches, roomSearch]);

  const matchByJoinCode = useMemo(
    () => allMatches.find((m) => String(m.id) === String(joinId || '').trim()),
    [allMatches, joinId]
  );

  const canCreate = useMemo(() => {
    const bal = Number(user?.balance ?? 0);
    const tc = Number(timeControlSeconds);
    const tcValid = options.some((o) => o.seconds === tc);
    if (!tcValid) return false;

    if (mode === 'friendly') return true;

    const w = Number(wager);
    return Number.isFinite(w) && w >= minimumWager && bal >= w;
  }, [mode, wager, user, timeControlSeconds, options, minimumWager]);

  const getBotLevelForRating = (ratingValue) => {
    const rating = Number(ratingValue || 0);
    if (!Number.isFinite(rating) || rating <= 1100) return 1;
    if (rating <= 1500) return 2;
    return 3;
  };

  const pickQuickBotName = (level) => {
    const names = QUICK_BOT_NAMES_BY_LEVEL[level] || QUICK_BOT_NAMES_BY_LEVEL[1];
    return names[Math.floor(Math.random() * names.length)] || 'PawnPilot';
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleQuickSearch = async () => {
    if (!hasUserId) return;
    try {
      setQuickSearching(true);
      setError('');

      const openRes = await chessAPI.listOpenMatches('friendly');
      const openMatches = Array.isArray(openRes.data) ? openRes.data : [];
      const candidate = openMatches.find((m) => Number(m.creator_id) !== userId);

      if (candidate) {
        await chessAPI.joinMatch(candidate.id);
        navigate(`/chess/duel/${candidate.id}`);
        return;
      }

      const ownOpen = openMatches.find((m) => Number(m.creator_id) === userId);
      const created = ownOpen || (await chessAPI.createMatch({ mode: 'friendly', wager: 0, timeControlSeconds })).data;
      const matchId = created.id;

      const waitMs = 7000;
      const pollMs = 1000;
      const startAt = Date.now();
      let opponentJoined = false;

      while (Date.now() - startAt < waitMs) {
        await sleep(pollMs);
        const matchRes = await chessAPI.getMatch(matchId);
        if (matchRes.data?.opponent_id) {
          opponentJoined = true;
          break;
        }
      }

      if (opponentJoined) {
        navigate(`/chess/duel/${matchId}`);
        return;
      }

      try {
        await chessAPI.cancelMatch(matchId);
      } catch {
        // ignore cancel failures for quick-search fallback
      }

      const level = getBotLevelForRating(user?.chess_rating);
      const botName = pickQuickBotName(level);
      navigate(`/chess/practice?bot=1&level=${level}&botName=${encodeURIComponent(botName)}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Quick search failed');
    } finally {
      setQuickSearching(false);
    }
  };

  const loadOpen = async () => {
    try {
      const [friendlyRes, bettingRes] = await Promise.allSettled([
        chessAPI.listOpenMatches('friendly'),
        chessAPI.listOpenMatches('betting'),
      ]);
      const combined = [];
      if (friendlyRes.status === 'fulfilled') combined.push(...(friendlyRes.value.data || []));
      if (bettingRes.status === 'fulfilled') combined.push(...(bettingRes.value.data || []));
      setOpenMatches(combined);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error;
      if (status === 401) setError('Session expired. Please login again.');
      else if (msg) setError(msg);
    }
  };

  const loadChallenges = async () => {
    try {
      const res = await chessAPI.listChallenges();
      setChallenges(res.data || []);
    } catch {
      // non-fatal
    }
  };

  const loadOngoing = async () => {
    try {
      const res = await chessAPI.listOngoingMatchesAll();
      setOngoingMatches(res.data || []);
    } catch {
      // non-fatal
    }
  };

  const loadFriends = async () => {
    try {
      const res = await api.get('/friends');
      setFriends(res.data || []);
    } catch {
      // non-fatal
    }
  };

  const loadAll = async () => {
    try {
      setError('');
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing(), loadFriends()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const id = setInterval(() => {
      void loadOpen();
      void loadChallenges();
      void loadOngoing();
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareMatchLink = async (matchId, modeLabel = 'challenge') => {
    if (!matchId) return;
    const link = buildInviteLink(`/chess/duel/${matchId}`);
    const message = `Join my Chess ${modeLabel}: ${link}`;
    try {
      if (false /* disabled navigator.share for desktop */) {
        await navigator.share({ title: 'Chess Invite', text: message, url: link });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        toast.success('Invite link copied!');
      } else {
        window.prompt('Share this Chess invite link:', link);
      }
    } catch {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        toast.success('Invite link copied!');
        } else {
          window.prompt('Share this Chess invite link:', link);
        }
      } catch {
        window.prompt('Share this Chess invite link:', link);
      }
    }
  };

  const handleCreate = async (overrideMode = null, opts = {}) => {
    try {
      setError('');
      const opp = String(opponentId || '').trim();
      const safeOpponentId = opp ? Number(opp) : null;

      const selectedMode = overrideMode || mode;
      if (overrideMode && overrideMode !== mode) {
        setMode(overrideMode);
      }
      const w = selectedMode === 'betting' ? Math.max(0, Number(wager || 0)) : 0;
      const tc = Number(timeControlSeconds);
      if (selectedMode === 'betting' && w < minimumWager) {
        setError(`Wager must be at least ${minimumWager} wikipenny`);
        return false;
      }

      const res = await chessAPI.createMatch({ mode: selectedMode, wager: w, timeControlSeconds: tc, opponentId: safeOpponentId });
      const shouldShare = Boolean(opts.shareAfterCreate);
      if (shouldShare) {
        await shareMatchLink(res.data.id, selectedMode === 'betting' ? 'bet room' : 'friendly room');
      }
      refreshUser?.();
      navigate(`/chess/duel/${res.data.id}`);
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create match');
      return false;
    }
  };

  const getMatchFlags = (m) => {
    const meId = Number(user?.id || 0);
    const isCreator = Number(m.creator_id) === meId;
    const isOpponent = Number(m.opponent_id) === meId;
    const isParticipant = isCreator || isOpponent;
    const isOpen = !m.opponent_id;
    const isPending = m.status === 'pending';
    const isOngoing = m.status === 'in_progress';
    const isAccepted = Boolean(m.accepted_at);
    const isBetting = Number(m.wager || 0) > 0 || String(m.mode || '').toLowerCase() === 'betting';
    const needsAccept = isOpponent && !isAccepted && isPending;
    const canCancel = isCreator && isPending;
    const canContinue = isParticipant && isOngoing;
    const canOpen = isParticipant && isPending;
    const canJoin = isPending && ((isOpen && !isCreator && !isAccepted) || needsAccept);
    const canWatch = (isAccepted || isOngoing) && !isParticipant;

    return {
      isCreator,
      isOpponent,
      isParticipant,
      isPending,
      isOngoing,
      isBetting,
      needsAccept,
      canCancel,
      canContinue,
      canOpen,
      canJoin,
      canWatch,
    };
  };

  const cancelMatch = async (matchId) => {
    try {
      setError('');
      await chessAPI.cancelMatch(matchId);
      await loadAll();
      refreshUser?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to cancel');
    }
  };

  const joinMatch = async (m, needsAccept) => {
    try {
      setError('');
      if (needsAccept) await chessAPI.acceptMatch(m.id);
      else await chessAPI.joinMatch(m.id);
      await loadAll();
      refreshUser?.();
      navigate(`/chess/duel/${m.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to join');
    }
  };

  const openCreateModal = (kind) => {
    setError('');
    setCreateKind(kind);
    if (kind === CREATE_KIND_REAL) setMode('betting');
    else setMode('friendly');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const handleCreateFromModal = async () => {
    if (createKind === CREATE_KIND_BOT) {
      const level = getBotLevelForRating(user?.chess_rating);
      navigate(`/chess/practice?bot=1&level=${level}`);
      closeCreateModal();
      return;
    }

    const targetMode = createKind === CREATE_KIND_REAL ? 'betting' : 'friendly';
    const created = await handleCreate(targetMode);
    if (created) closeCreateModal();
  };

  const onJoinByCode = async () => {
    const code = String(joinId || '').trim();
    if (!code) return;

    const match = allMatches.find((m) => String(m.id) === code);
    if (!match) {
      setError('Match not found');
      return;
    }

    const flags = getMatchFlags(match);
    if (flags.canJoin) {
      await joinMatch(match, flags.needsAccept);
      return;
    }

    if (flags.canOpen || flags.canContinue || flags.canWatch) {
      navigate(`/chess/duel/${match.id}`);
      return;
    }

    setError('You cannot join this match right now.');
  };

  const createTitle =
    createKind === CREATE_KIND_BOT
      ? 'Vs Bot Challenge'
      : createKind === CREATE_KIND_REAL
        ? 'Real Challenge'
        : 'Friendly Challenge';

  const isRealChallenge = createKind === CREATE_KIND_REAL;

  const getStatusBadge = (status, isOngoing) => {
    if (isOngoing) return <span className="status-badge ongoing">In Progress</span>;
    if (status === 'pending') return <span className="status-badge pending">Waiting</span>;
    return <span className="status-badge completed">Completed</span>;
  };

  const getTimeLabel = (seconds) => {
    const mins = Math.round(Number(seconds || 0) / 60);
    return `${mins} min${mins !== 1 ? 's' : ''}`;
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon">♟️</div>
            <div>
              <h1 className="page-title">Chess Arena</h1>
              <p className="page-subtitle">Challenge players, climb the ranks, prove your strategy</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              ← Back to Dashboard
            </button>
            <button className="secondary-btn" onClick={() => navigate('/chess/tournaments')}>
              🏆 Tournaments
            </button>
          </div>
        </div>

        {/* User Stats Card */}
        <div className="user-stats-card">
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-label">Balance</div>
                <div className="stat-value">{user?.balance ?? 0} credits</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <div className="stat-label">Chess Rating</div>
                <div className="stat-value">{user?.chess_rating ?? 1200} ELO</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">🎯</div>
              <div className="stat-info">
                <div className="stat-label">Active Games</div>
                <div className="stat-value">{myCreatedMatches.filter(m => m.status === 'in_progress').length + myJoinedMatches.filter(m => m.status === 'in_progress').length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="quick-actions-card">
          <div className="quick-actions-row">
            <button 
              className={`quick-action ${quickSearching ? 'searching' : ''}`}
              disabled={!hasUserId || quickSearching}
              onClick={handleQuickSearch}
            >
              {quickSearching ? (
                <>
                  <span className="spinner-small"></span>
                  Searching...
                </>
              ) : (
                <>
                  <span className="action-icon">⚡</span>
                  Quick Match
                </>
              )}
            </button>
            
            <div className="join-by-code">
              <input
                className="code-input"
                value={joinId}
                onChange={(e) => setJoinId(e.target.value)}
                placeholder="Enter match ID"
              />
              <button className="join-btn" disabled={!joinId} onClick={onJoinByCode}>
                Join Match
              </button>
            </div>
          </div>
          
          {matchByJoinCode && (
            <div className="match-found">
              <span className="found-icon">🎯</span>
              Match found: {matchByJoinCode.creator_username || 'Host'} vs {matchByJoinCode.opponent_username || 'Open'}
            </div>
          )}
        </div>

        {/* Create Challenge Cards */}
        <div className="create-challenges">
          <div className="section-header">
            <h2 className="section-title">Create Challenge</h2>
            <p className="section-subtitle">Choose your game mode</p>
          </div>
          
          <div className="create-cards">
            <div className="create-card bot" onClick={() => openCreateModal(CREATE_KIND_BOT)}>
              <div className="create-icon">🤖</div>
              <div className="create-info">
                <div className="create-title">Vs Bot</div>
                <div className="create-desc">Practice against AI</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
            
            <div className="create-card friendly" onClick={() => openCreateModal(CREATE_KIND_FRIENDLY)}>
              <div className="create-icon">👥</div>
              <div className="create-info">
                <div className="create-title">Friendly Challenge</div>
                <div className="create-desc">Play with friends, no wager</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
            
            <div className="create-card real" onClick={() => openCreateModal(CREATE_KIND_REAL)}>
              <div className="create-icon">💰</div>
              <div className="create-info">
                <div className="create-title">Real Challenge</div>
                <div className="create-desc">Win credits, climb ranks</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
          </div>
        </div>

        {/* Matches Tabs */}
        <div className="matches-section">
          <div className="matches-tabs">
            <button 
              className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
              onClick={() => setActiveTab('my')}
            >
              <span className="tab-icon">🎯</span>
              <span className="tab-text">My Games</span>
              <span className="tab-count">{myCreatedMatches.length + myJoinedMatches.length}</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'available' ? 'active' : ''}`}
              onClick={() => setActiveTab('available')}
            >
              <span className="tab-icon">🌍</span>
              <span className="tab-text">Available</span>
              <span className="tab-count">{availableMatches.length}</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'live' ? 'active' : ''}`}
              onClick={() => setActiveTab('live')}
            >
              <span className="tab-icon">👁️</span>
              <span className="tab-text">Live Games</span>
              <span className="tab-count">{liveMatches.length}</span>
            </button>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading matches...</span>
            </div>
          )}

          {!loading && activeTab === 'my' && (
            <>
              {/* My Created Matches */}
              {myCreatedMatches.length > 0 && (
                <div className="matches-group">
                  <h3 className="group-title">Created by you</h3>
                  <div className="matches-list">
                    {myCreatedMatches.map((m) => {
                      const flags = getMatchFlags(m);
                      return (
                        <div key={`created-${m.id}`} className="match-card">
                          <div className="match-header">
                            <div className="match-id">Match #{m.id}</div>
                            <div className="match-badges">
                              <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                                {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                              </span>
                              {flags.isBetting && (
                                <span className="wager-badge">Wager: {m.wager}</span>
                              )}
                              {getStatusBadge(m.status, flags.isOngoing)}
                            </div>
                          </div>
                          <div className="match-players">
                            <div className="player">
                              <span className="player-role">You (Creator)</span>
                              <span className="player-name">{m.creator_username || 'You'}</span>
                            </div>
                            <div className="vs-divider">VS</div>
                            <div className="player">
                              <span className="player-role">Opponent</span>
                              <span className="player-name">{m.opponent_username || 'Waiting...'}</span>
                            </div>
                          </div>
                          <div className="match-details">
                            <span className="detail-item">⏱️ {getTimeLabel(m.time_control_seconds)}</span>
                            <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="match-invite-row">
                            <button
                              className="action-btn watch"
                              onClick={() => shareMatchLink(m.id, flags.isBetting ? 'bet room' : 'friendly room')}
                              title="Copy invite link"
                              aria-label="Copy invite link"
                            >
                              📋
                            </button>
                          </div>
                          <div className="match-actions">
                            {flags.canCancel && (
                              <button className="action-btn cancel" onClick={() => cancelMatch(m.id)}>
                                Cancel
                              </button>
                            )}
                            {(flags.canOpen || flags.canContinue) && (
                              <button className="action-btn play" onClick={() => navigate(`/chess/duel/${m.id}`)}>
                                {flags.canContinue ? 'Continue →' : 'Open →'}
                              </button>
                            )}
                            {flags.canWatch && (
                              <button className="action-btn watch" onClick={() => navigate(`/chess/duel/${m.id}`)}>
                                Watch →
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* My Joined Matches */}
              {myJoinedMatches.length > 0 && (
                <div className="matches-group">
                  <h3 className="group-title">Joined by you</h3>
                  <div className="matches-list">
                    {myJoinedMatches.map((m) => {
                      const flags = getMatchFlags(m);
                      return (
                        <div key={`joined-${m.id}`} className="match-card">
                          <div className="match-header">
                            <div className="match-id">Match #{m.id}</div>
                            <div className="match-badges">
                              <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                                {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                              </span>
                              {flags.isBetting && (
                                <span className="wager-badge">Wager: {m.wager}</span>
                              )}
                              {getStatusBadge(m.status, flags.isOngoing)}
                            </div>
                          </div>
                          <div className="match-players">
                            <div className="player">
                              <span className="player-role">Host</span>
                              <span className="player-name">{m.creator_username || 'Unknown'}</span>
                            </div>
                            <div className="vs-divider">VS</div>
                            <div className="player">
                              <span className="player-role">You</span>
                              <span className="player-name">You</span>
                            </div>
                          </div>
                          <div className="match-details">
                            <span className="detail-item">⏱️ {getTimeLabel(m.time_control_seconds)}</span>
                            <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="match-actions">
                            {(flags.canOpen || flags.canContinue) && (
                              <button className="action-btn play" onClick={() => navigate(`/chess/duel/${m.id}`)}>
                                {flags.canContinue ? 'Continue →' : 'Open →'}
                              </button>
                            )}
                            {flags.canWatch && (
                              <button className="action-btn watch" onClick={() => navigate(`/chess/duel/${m.id}`)}>
                                Watch →
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {myCreatedMatches.length === 0 && myJoinedMatches.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">♟️</div>
                  <div className="empty-title">No active games</div>
                  <div className="empty-desc">Create a challenge or join an available game</div>
                </div>
              )}
            </>
          )}

          {!loading && activeTab === 'available' && (
            <>
              <div className="search-bar">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  value={roomSearch}
                  onChange={(e) => setRoomSearch(e.target.value)}
                  placeholder="Search by match ID or player name..."
                />
                {roomSearch && (
                  <button className="clear-search" onClick={() => setRoomSearch('')}>×</button>
                )}
              </div>

              {filteredAvailableMatches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🌍</div>
                  <div className="empty-title">No available challenges</div>
                  <div className="empty-desc">
                    {roomSearch ? `No matches matching "${roomSearch}"` : 'Create a challenge to start playing'}
                  </div>
                  {roomSearch && (
                    <button className="clear-filters-btn" onClick={() => setRoomSearch('')}>
                      Clear Search
                    </button>
                  )}
                </div>
              ) : (
                <div className="matches-list">
                  {filteredAvailableMatches.map((m) => {
                    const flags = getMatchFlags(m);
                    return (
                      <div key={`available-${m.id}`} className="match-card available">
                        <div className="match-header">
                          <div className="match-id">Match #{m.id}</div>
                          <div className="match-badges">
                            <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                              {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                            </span>
                            {flags.isBetting && (
                              <span className="wager-badge">Wager: {m.wager}</span>
                            )}
                          </div>
                        </div>
                        <div className="match-players">
                          <div className="player">
                            <span className="player-role">Host</span>
                            <span className="player-name">{m.creator_username || 'Unknown'}</span>
                          </div>
                          <div className="vs-divider">VS</div>
                          <div className="player">
                            <span className="player-role">Open</span>
                            <span className="player-name waiting">Waiting for opponent</span>
                          </div>
                        </div>
                        <div className="match-details">
                          <span className="detail-item">⏱️ {getTimeLabel(m.time_control_seconds)}</span>
                          <span className="detail-item">👤 Rating: {m.creator_rating || '?'}</span>
                          <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="match-actions">
                          {flags.canJoin && (
                            <button className="action-btn join" onClick={() => joinMatch(m, flags.needsAccept)}>
                              Join Challenge →
                            </button>
                          )}
                          {flags.canWatch && (
                            <button className="action-btn watch" onClick={() => navigate(`/chess/duel/${m.id}`)}>
                              Watch →
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!loading && activeTab === 'live' && (
            <>
              <div className="search-bar">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  value={roomSearch}
                  onChange={(e) => setRoomSearch(e.target.value)}
                  placeholder="Search by match ID or player name..."
                />
                {roomSearch && (
                  <button className="clear-search" onClick={() => setRoomSearch('')}>×</button>
                )}
              </div>

              {filteredLiveMatches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📺</div>
                  <div className="empty-title">No live matches</div>
                  <div className="empty-desc">
                    {roomSearch ? `No live matches matching "${roomSearch}"` : 'No chess games are currently in progress.'}
                  </div>
                </div>
              ) : (
                <div className="matches-list">
                  {filteredLiveMatches.map((m) => {
                    const flags = getMatchFlags(m);
                    return (
                      <div key={`live-${m.id}`} className="match-card live">
                        <div className="match-header">
                          <div className="match-id">Match #{m.id}</div>
                          <div className="match-badges">
                            <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                              {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                            </span>
                            {flags.isBetting && (
                              <span className="wager-badge">Wager: {m.wager}</span>
                            )}
                            <span className="status-badge ongoing">In Progress</span>
                          </div>
                        </div>
                        <div className="match-players">
                          <div className="player">
                            <span className="player-role">White</span>
                            <span className="player-name">{m.white_username || m.creator_username || 'Unknown'}</span>
                          </div>
                          <div className="vs-divider">VS</div>
                          <div className="player">
                            <span className="player-role">Black</span>
                            <span className="player-name">{m.black_username || m.opponent_username || 'Unknown'}</span>
                          </div>
                        </div>
                        <div className="match-details">
                          <span className="detail-item">⏱️ {getTimeLabel(m.time_control_seconds)}</span>
                          <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="match-actions">
                          <button className="action-btn watch" onClick={() => navigate(`/chess/duel/${m.id}`)}>
                            Watch →
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal-container chess-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-icon">♟️</div>
                <h3 className="modal-title">{createTitle}</h3>
                <button className="modal-close" onClick={closeCreateModal}>×</button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Time Control</label>
                  <div className="time-options">
                    {options.map((o) => (
                      <button
                        key={o.seconds}
                        className={`time-option ${timeControlSeconds === o.seconds ? 'selected' : ''}`}
                        onClick={() => setTimeControlSeconds(o.seconds)}
                      >
                        <span className="time-icon">{o.icon}</span>
                        <span className="time-label">{o.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {isRealChallenge && (
                  <div className="form-group">
                    <label className="form-label">Wager Amount</label>
                    <div className="wager-input-wrapper">
                      <span className="wager-currency">💰</span>
                      <input
                        type="number"
                        className="form-input wager-input"
                        min={minimumWager}
                        max={user?.balance ?? 0}
                        value={wager}
                        onChange={(e) => setWager(Number(e.target.value))}
                      />
                      <span className="wager-suffix">credits</span>
                    </div>
                    <div className="form-hint">
                      Minimum: {minimumWager} wikipenny • Balance: {user?.balance ?? 0} credits
                    </div>
                    {!canCreate && isRealChallenge && (
                      <div className="error-hint">
                        {Number(wager) < minimumWager
                          ? `⚠️ Minimum wager is ${minimumWager} wikipenny`
                          : '⚠️ Insufficient balance for this wager'}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Invite Friend (Optional)</label>
                  <select
                    className="form-select"
                    value={opponentId}
                    onChange={(e) => setOpponentId(e.target.value)}
                    disabled={createKind === CREATE_KIND_BOT}
                  >
                    <option value="">Open match (anyone can join)</option>
                    {friends.map((f) => (
                      <option key={f.friendship_id || f.friend_id} value={f.friend_id}>
  {Boolean(friendsOnlineStatus[f.friend_id]) ? '🟢' : '⚪'} | {f.friend_username || f.username || 'Friend'}
</option>
                    ))}
                  </select>
                  <div className="form-hint">Send a direct challenge to a specific friend</div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  className="btn-create"
                  disabled={quickSearching || (isRealChallenge && !canCreate)}
                  onClick={handleCreateFromModal}
                >
                  {createKind === CREATE_KIND_BOT ? 'Start vs Bot →' : 'Create Challenge →'}
                </button>
                {createKind !== CREATE_KIND_BOT && (
                  <button
                    className="join-btn"
                    disabled={quickSearching || (isRealChallenge && !canCreate)}
                    onClick={async () => {
                      const targetMode = createKind === CREATE_KIND_REAL ? 'betting' : 'friendly';
                      const created = await handleCreate(targetMode, { shareAfterCreate: true });
                      if (created) closeCreateModal();
                    }}
                  >
                    Create & Copy Link
                  </button>
                )}
                <button className="btn-cancel" onClick={closeCreateModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
